import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Text, Button } from '@theneatcompany/nui'
import { FormattedMessage } from 'react-intl'
import { Form, Formik } from 'formik'

import ModularizedPlanCard from '@/components/common/ModularizedPlanCard/ModularizedPlanCard'
import { formatPlan } from '@/utils/plans'
import { useDialogs } from '@/components/Dialogs/DialogsContext'
import { useAccounts } from '@/components/Providers/AccountsProvider'
import { sendError } from '@/utils/rollbar'
import { ORDER_ACTIVATION_TYPES, PLAN_SKU, SUBSCRIPTION_STATUSES } from '@/constants'

import styles from './ModularizedPlanCards.module.scss'

export default function ModularizedPlanCards({ isResubscribe = false }) {
  const [totalPrice, setTotalPrice] = useState(0)
  const [selectedModules, setSelectedModules] = useState({})
  const [canPurchaseNeatModule, setCanPurchaseNeatModule] = useState(false)
  const [hasAdditionalSolutions, setHasAdditionalSolutions] = useState(false)
  const [availableModules, setAvailableModules] = useState([])
  const dialogs = useDialogs()
  const { user, subscriptions: [base], paymentMethod, findPlanBySKU } = useAccounts()

  const BASE_MODULE_TYPE = 'base'

  let upgradePathSku = base?.planSku
  if (isResubscribe && !upgradePathSku.startsWith('neathome')) {
    upgradePathSku = PLAN_SKU.NEATYR
  }

  const defaultUpgradePath = findPlanBySKU(upgradePathSku)

  const basePlan = useMemo(() => findPlanBySKU(base?.planSku), [base])
  const isExpired = useMemo(() => base.status === SUBSCRIPTION_STATUSES.EXPIRED, [base])

  const getAvailableModules = () => {
    const modules = []

    setTotalPrice(0)
    setCanPurchaseNeatModule(false)

    // add modules available to transition to
    basePlan?.availablePlanTransitions.forEach(plan => {
      findPlanBySKU(plan.sku)?.modules.forEach(module => {
        if (!modules.some(mod => mod.type === module.type)) {
          modules.push(module)
        }
      })
    })

    // add current modules
    basePlan.modules.forEach(module => {
      if (!modules.some(mod => mod.type === module.type)) {
        modules.push(module)
      }
    })

    if (modules.length > 1) setHasAdditionalSolutions(true)

    const baseNeatModule = basePlan.modules.find(module => module.type === BASE_MODULE_TYPE)
    const availableNeatModule = modules.find(module => module.type === BASE_MODULE_TYPE)

    if (baseNeatModule?.name !== availableNeatModule?.name
      || isExpired) setCanPurchaseNeatModule(true)

    modules.forEach(module => {
      if (module.type === BASE_MODULE_TYPE) {
        // Base module is always required and should set total price
        setSelectedModules(v => ({ ...v, [module.type]: true }))
        setTotalPrice(modules.find(mod => mod.type === BASE_MODULE_TYPE)?.price)
      } else if (basePlan?.modules.some(mod => module.type === mod.type)
        && !isExpired
      ) {
        // Add current additional services
        setSelectedModules(v => ({ ...v, [module.type]: true }))
        setTotalPrice(total => total + module.price)
      } else {
        setSelectedModules(v => ({ ...v, [module.type]: false }))
      }
    })

    setAvailableModules(modules.sort((a, b) => a.sortOrder - b.sortOrder))
  }

  useEffect(() => {
    getAvailableModules()
  }, [])

  useEffect(() => {
    getAvailableModules()
  }, [base])

  const getInitialTotalPrice = () => availableModules.reduce((total, module) => (
    selectedModules[module.type] ? total + module.price : total
  ), 0)

  const handleUpdatePlan = (values, { resetForm }) => {
    const dialogId = paymentMethod && user.integrations?.recurlyId
      ? 'subscribeWithPaymentMethod' : 'subscribeWithoutPaymentMethod'
    const planTransitions = isExpired && basePlan?.isModularized
      ? [...basePlan.availablePlanTransitions, { sku: basePlan.sku.replace('mo', 'yr') }]
      : basePlan?.availablePlanTransitions

    const transitionPlan = planTransitions
      ?.map(({ sku, activationType }) => (
        {
          ...findPlanBySKU(sku),
          activationType: !isExpired ? activationType : ORDER_ACTIVATION_TYPES.NOW,
        }))
      .find(({ modules }) => Object.entries(values).every(([key, value]) => {
        if (value) return modules.some(m => m.type === key)
        return !modules.some(m => m.type === key)
      }))

    if (!transitionPlan && !isResubscribe) {
      const errorMsg = 'Did not find the transition plan'
      sendError(errorMsg)
      return
    }

    dialogs.open(dialogs.refs[dialogId], {
      plan: formatPlan(transitionPlan ?? { ...defaultUpgradePath, activationType: ORDER_ACTIVATION_TYPES.NOW }),
      hasModularizedView: true,
      onClose: () => {
        resetForm()
        setTotalPrice(getInitialTotalPrice())
      },
    })
  }

  return (
    <Formik
      onSubmit={handleUpdatePlan}
      initialValues={selectedModules}
      enableReinitialize
    >
      {({ dirty }) => (
        <Form className={styles.form}>
          {availableModules.map((module) => (
            <ModularizedPlanCard
              key={module.type}
              module={module}
              onChange={setTotalPrice}
              hasAdditionalSolutions={hasAdditionalSolutions}
            />
          ))}
          <div className={styles.footer}>
            <div>
              <Text color="foreground-secondary">
                <FormattedMessage id="totalCost" />:
              </Text>
              <Text color="tint-brand-primary" typeStyle="bold">
                {` $${totalPrice}/`}
                <FormattedMessage id="yearly" />
              </Text>
            </div>
            <Button type="submit" disabled={isResubscribe || canPurchaseNeatModule ? false : !dirty}>
              <FormattedMessage id={`button.${isResubscribe ? 'subscribe' : 'updatePlan'}`} />
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

ModularizedPlanCards.propTypes = {
  isResubscribe: PropTypes.bool,
}
